import React, { useState, useEffect } from 'react';
import { X, CreditCard, Loader2 } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import type { Stripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { useBilling } from '../hooks/useBilling';
import './Modal.css';

interface PaymentMethodModalProps {
  onClose: () => void;
}

const PaymentForm: React.FC<{ clientSecret: string; onSuccess: () => void }> = ({ 
  clientSecret, 
  onSuccess 
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { confirmSetupIntent } = useBilling();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    setError('');

    const card = elements.getElement(CardElement);
    if (!card) {
      setError('Card element not found');
      setLoading(false);
      return;
    }

    try {
      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: { card }
      });

      if (stripeError) {
        setError(stripeError.message || 'Payment setup failed');
        setLoading(false);
        return;
      }

      if (setupIntent && setupIntent.payment_method) {
        const success = await confirmSetupIntent(
          setupIntent.id,
          setupIntent.payment_method as string
        );
        if (success) {
          onSuccess();
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Payment setup failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="card-element-wrapper">
        <CardElement 
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#424770',
                '::placeholder': { color: '#aab7c4' }
              }
            }
          }}
        />
      </div>
      {error && <div className="error-message">{error}</div>}
      
      <div style={{ marginTop: '20px', textAlign: 'right' }}>
        <button type="submit" disabled={loading || !stripe} className="btn btn-primary">
          {loading ? (
            <>
              <Loader2 size={16} className="spinning" />
              Processing...
            </>
          ) : (
            'Save Payment Method'
          )}
        </button>
      </div>
    </form>
  );
};

const PaymentMethodModal: React.FC<PaymentMethodModalProps> = ({ onClose }) => {
  const { createSetupIntent } = useBilling();
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [clientSecret, setClientSecret] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const setupPayment = async () => {
      try {
        const intent = await createSetupIntent();
        if (intent) {
          setClientSecret(intent.client_secret);
          setStripePromise(loadStripe(intent.publishable_key));
        } else {
          setError('Failed to initialize payment setup');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to setup payment');
      } finally {
        setLoading(false);
      }
    };
    setupPayment();
  }, [createSetupIntent]);

  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Add Payment Method</h2>
            <button onClick={onClose} className="modal-close">
              <X size={20} />
            </button>
          </div>
          <div className="modal-body">
            <div className="loading-state">
              <Loader2 size={32} className="spinning" />
              <p>Setting up payment...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Payment Setup Error</h2>
            <button onClick={onClose} className="modal-close">
              <X size={20} />
            </button>
          </div>
          <div className="modal-body">
            <div className="error-state">
              <p>Failed to setup payment: {error}</p>
              <button onClick={onClose} className="btn-secondary">
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Add Payment Method</h2>
          <button onClick={onClose} className="modal-close">
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          <div className="payment-info">
            <CreditCard size={32} />
            <p>Your card will be saved securely for future charges.</p>
          </div>

          {stripePromise && clientSecret && (
            <Elements stripe={stripePromise}>
              <PaymentForm clientSecret={clientSecret} onSuccess={onClose} />
            </Elements>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentMethodModal;